<script setup>
import { computed, inject, ref, watch, onMounted } from 'vue'
import { useForm, Field, ErrorMessage } from 'vee-validate'
import { useCartStore } from '../stores/cartStore'
import '../helpers/validators'
import * as yup from 'yup'

const props = defineProps({
  formMetadata: {
    type: Object,
    required: true
  }
})

const cartStore = useCartStore()
const formValues = inject('formValues', {})
const parentSetFieldValue = inject('setFieldValue', null)
const editMode = inject('editMode', ref(false))

const agentTypeChoices = [
  { id: 'P', name: 'Person', tag: 'option' },
  { id: 'C', name: 'Corporation', tag: 'option' }
]

const stateChoices = [
  { id: 'AL', name: 'Alabama', tag: 'option' },
  { id: 'AK', name: 'Alaska', tag: 'option' },
  { id: 'AZ', name: 'Arizona', tag: 'option' },
  { id: 'AR', name: 'Arkansas', tag: 'option' },
  { id: 'CA', name: 'California', tag: 'option' },
  { id: 'CO', name: 'Colorado', tag: 'option' },
  { id: 'CT', name: 'Connecticut', tag: 'option' },
  { id: 'DE', name: 'Delaware', tag: 'option' },
  { id: 'FL', name: 'Florida', tag: 'option' },
  { id: 'GA', name: 'Georgia', tag: 'option' },
  { id: 'HI', name: 'Hawaii', tag: 'option' },
  { id: 'ID', name: 'Idaho', tag: 'option' },
  { id: 'IL', name: 'Illinois', tag: 'option' },
  { id: 'IN', name: 'Indiana', tag: 'option' },
  { id: 'IA', name: 'Iowa', tag: 'option' },
  { id: 'KS', name: 'Kansas', tag: 'option' },
  { id: 'KY', name: 'Kentucky', tag: 'option' },
  { id: 'LA', name: 'Louisiana', tag: 'option' },
  { id: 'ME', name: 'Maine', tag: 'option' },
  { id: 'MD', name: 'Maryland', tag: 'option' },
  { id: 'MA', name: 'Massachusetts', tag: 'option' },
  { id: 'MI', name: 'Michigan', tag: 'option' },
  { id: 'MN', name: 'Minnesota', tag: 'option' },
  { id: 'MS', name: 'Mississippi', tag: 'option' },
  { id: 'MO', name: 'Missouri', tag: 'option' },
  { id: 'MT', name: 'Montana', tag: 'option' },
  { id: 'NE', name: 'Nebraska', tag: 'option' },
  { id: 'NV', name: 'Nevada', tag: 'option' },
  { id: 'NH', name: 'New Hampshire', tag: 'option' },
  { id: 'NJ', name: 'New Jersey', tag: 'option' },
  { id: 'NM', name: 'New Mexico', tag: 'option' },
  { id: 'NY', name: 'New York', tag: 'option' },
  { id: 'NC', name: 'North Carolina', tag: 'option' },
  { id: 'ND', name: 'North Dakota', tag: 'option' },
  { id: 'OH', name: 'Ohio', tag: 'option' },
  { id: 'OK', name: 'Oklahoma', tag: 'option' },
  { id: 'OR', name: 'Oregon', tag: 'option' },
  { id: 'PA', name: 'Pennsylvania', tag: 'option' },
  { id: 'RI', name: 'Rhode Island', tag: 'option' },
  { id: 'SC', name: 'South Carolina', tag: 'option' },
  { id: 'SD', name: 'South Dakota', tag: 'option' },
  { id: 'TN', name: 'Tennessee', tag: 'option' },
  { id: 'TX', name: 'Texas', tag: 'option' },
  { id: 'UT', name: 'Utah', tag: 'option' },
  { id: 'VT', name: 'Vermont', tag: 'option' },
  { id: 'VA', name: 'Virginia', tag: 'option' },
  { id: 'WA', name: 'Washington', tag: 'option' },
  { id: 'WV', name: 'West Virginia', tag: 'option' },
  { id: 'WI', name: 'Wisconsin', tag: 'option' },
  { id: 'WY', name: 'Wyoming', tag: 'option' },
  { id: 'DC', name: 'District of Columbia', tag: 'option' }
]

// Track the current agent type for dynamic validation
const currentAgentType = ref(null)

// Get initial values from the cart store
const initialValues = computed(() => {
  return {
    registered_agent_type: formValues.registered_agent_type || '',
    registered_agent_first_name: formValues.registered_agent_first_name || '',
    registered_agent_middle_name: formValues.registered_agent_middle_name || '',
    registered_agent_last_name: formValues.registered_agent_last_name || '',
    registered_agent_name: formValues.registered_agent_name || '',
    registered_agent_signature: formValues.registered_agent_signature || '',
    registered_agent_address_1: formValues.registered_agent_address_1 || '',
    registered_agent_address_2: formValues.registered_agent_address_2 || '',
    registered_agent_city: formValues.registered_agent_city || '',
    registered_agent_state: formValues.registered_agent_state || '',
    registered_agent_zip: formValues.registered_agent_zip || ''
  }
})

// Add a computed property to check for changes
const hasChanges = computed(() => {
  // Compare current values with initial values
  const initialValues = cartStore.cartData.initialFormData || {}
  const relevantFields = [
    'registered_agent_type',
    'registered_agent_first_name',
    'registered_agent_middle_name',
    'registered_agent_last_name',
    'registered_agent_name',
    'registered_agent_address_1',
    'registered_agent_address_2',
    'registered_agent_city',
    'registered_agent_state',
    'registered_agent_zip'
  ]

  // Guard against undefined values
  if (!values) {
    return false
  }

  // Compare each field's current value with its initial value
  for (const field of relevantFields) {
    const currentValue = values[field]
    const initialValue = initialValues[field]

    // Normalize values for comparison
    const normalizeValue = (val) =>
      val === undefined || val === null ? '' : String(val).trim()
    const normalizedCurrent = normalizeValue(currentValue)
    const normalizedInitial = normalizeValue(initialValue)

    if (normalizedCurrent !== normalizedInitial) {
      return true
    }
  }

  return false
})

// Define validation schema using Yup
const validationSchema = computed(() => {
  // Create base schema
  const schema = {
    registered_agent_type: yup
      .string()
      .required('Please select an agent type')
      .oneOf(['P', 'C'], 'Invalid agent type'),

    registered_agent_first_name: yup.string().when('registered_agent_type', {
      is: 'P',
      then: (schema) =>
        schema.required(
          'First name is required for individual registered agents'
        ),
      otherwise: (schema) => schema.nullable()
    }),
    registered_agent_middle_name: yup.string().nullable(),
    registered_agent_last_name: yup.string().when('registered_agent_type', {
      is: 'P',
      then: (schema) =>
        schema.required(
          'Last name is required for individual registered agents'
        ),
      otherwise: (schema) => schema.nullable()
    }),

    registered_agent_name: yup.string().when('registered_agent_type', {
      is: 'C',
      then: (schema) =>
        schema
          .required('Company name is required for corporate registered agents')
          .max(300, 'Name cannot exceed 300 characters')
          .matches(
            /^[a-zA-Z0-9\s',.\-&]+$/,
            'Name contains invalid characters'
          ),
      otherwise: (schema) => schema.nullable()
    }),

    // Modified signature validation to only require when there are changes
    registered_agent_signature: yup.string().test({
      name: 'conditional-required',
      test: function (value) {
        // Only require signature if there are changes and we're in edit mode
        if (hasChanges.value && editMode.value) {
          if (!value) {
            return this.createError({
              message:
                'Please type your name to accept the designation of registered agent'
            })
          }
        }
        return true
      }
    }),

    // Rest of the validation schema remains the same
    registered_agent_address_1: yup
      .string()
      .required('Street address is required')
      .max(42, 'Address cannot exceed 42 characters')
      .test('no-po-box', 'P.O. Box addresses are not allowed', (value) => {
        if (!value) return true
        const poBoxPattern =
          /\b[P|p]?(OST|ost)?\.?\s*[O|o|0]?(ffice|FFICE)?\.?\s*[B|b][O|o|0]?[X|x]?\.?\s+[#]?(\d+)\b/i
        return !poBoxPattern.test(value)
      })
      .test(
        'physical-address',
        'Must be a physical street address',
        (value) => {
          if (!value) return true
          const nonPhysicalPattern = /\b(PMB|virtual|mail\s+stop|box|unit)\b/i
          return !nonPhysicalPattern.test(value)
        }
      ),

    registered_agent_address_2: yup
      .string()
      .max(100, 'Address line 2 cannot exceed 100 characters')
      .nullable(),

    registered_agent_city: yup
      .string()
      .required('City is required')
      .max(28, 'City name cannot exceed 28 characters')
      .matches(
        /^[a-zA-Z\s-]+$/,
        'City name can only contain letters, spaces, and hyphens'
      ),

    registered_agent_state: yup.string().required('State is required'),

    registered_agent_zip: yup
      .string()
      .required('ZIP code is required')
      .max(10, 'ZIP code cannot exceed 10 characters')
      .matches(/^\d{5}(-\d{4})?$/, 'Invalid ZIP code format')
  }

  return yup.object().shape(schema)
})

// Move useForm before the validation context
const { handleSubmit, setFieldValue, values, errors, validate, setFieldError } =
  useForm({
    validationSchema: validationSchema.value,
    initialValues: initialValues.value,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: false
  })

// Add hasChanges to validation context after useForm initialization
const validationContext = computed(() => {
  return { hasChanges: hasChanges.value }
})

// Update the form configuration to use the validation context
watch(validationContext, (newContext) => {}, { immediate: true })

// Register this form with the cart store
onMounted(() => {
  cartStore.registerForm('registeredAgentForm', {
    values,
    setFieldValue,
    validate
  })
})

// Watch for agent type changes to handle dynamic fields
watch(
  () => values.registered_agent_type,
  async (newType) => {
    currentAgentType.value = newType
    // Reset signature field when switching from corporate to individual
    if (newType === 'P') {
      setFieldValue('registered_agent_signature', null)
      setFieldValue('registered_agent_name', null)
    } else if (newType === 'C') {
      setFieldValue('registered_agent_first_name', null)
      setFieldValue('registered_agent_middle_name', null)
      setFieldValue('registered_agent_last_name', null)
    }
    // Validate after type change
    await validate()
  }
)

// Watch for changes in the form values and update the parent form values
watch(
  values,
  (newValues) => {
    if (parentSetFieldValue) {
      Object.entries(newValues).forEach(([key, value]) => {
        if (formValues[key] !== value) {
          parentSetFieldValue(key, value)
        }
      })
    }
  },
  { deep: true }
)

const onSubmit = handleSubmit((values) => {
  if (parentSetFieldValue) {
    Object.entries(values).forEach(([key, value]) => {
      if (formValues[key] !== value) {
        parentSetFieldValue(key, value)
      }
    })
  }
})

// Computed property to determine if signature fields should be shown
const showSignatureFields = computed(() => currentAgentType.value === 'C')

// Set initial agent type
currentAgentType.value = initialValues.value.registered_agent_type

// Expose validate method to parent component
defineExpose({
  validate: async () => {
    const result = await validate()

    // If validation failed
    if (!result.valid) {
      // Get all field names from the schema
      const fieldNames = Object.keys(validationSchema.value.fields)

      // Check each field for errors
      for (const fieldName of fieldNames) {
        // Check both result.errors and the errors ref
        if (result.errors[fieldName] || errors[fieldName]) {
          const errorMessage = result.errors[fieldName] || errors[fieldName]

          // Set the error on the field
          setFieldError(fieldName, errorMessage)

          return {
            valid: false,
            firstErrorField: fieldName,
            errors: result.errors || errors
          }
        }
      }
    }

    // Only reach here if no errors were found
    return {
      valid: true,
      firstErrorField: null,
      errors: {}
    }
  },
  setFieldError: (field, message) => {
    setFieldError(field, message)
  }
})
</script>

<template>
  <form
    @submit.prevent="onSubmit"
    class="row gx-3 gy-2 align-items-center cart-form registered-agent-form"
  >
    <!-- Agent Type Selection -->
    <div class="mb-3">
      <label for="registered_agent_type" class="form-label">Agent Type</label>
      <Field
        as="select"
        name="registered_agent_type"
        class="form-select"
        :class="{ 'is-invalid': errors.registered_agent_type }"
      >
        <option value="">Select agent type</option>
        <option
          v-for="choice in agentTypeChoices"
          :key="choice.id"
          :value="choice.id"
        >
          {{ choice.name }}
        </option>
      </Field>
      <div class="form-text">
        Select whether the registered agent is an individual person or a
        corporation.
      </div>
      <ErrorMessage name="registered_agent_type" class="text-danger" />
    </div>

    <!-- Individual Person Fields -->
    <template v-if="currentAgentType === 'P'">
      <div class="row">
        <div class="col-md-4 mb-3">
          <label for="registered_agent_last_name" class="form-label"
            >Last Name</label
          >
          <Field
            name="registered_agent_last_name"
            type="text"
            class="form-control"
            autocomplete="family-name"
          />
          <ErrorMessage name="registered_agent_last_name" class="text-danger" />
        </div>
        <div class="col-md-4 mb-3">
          <label for="registered_agent_first_name" class="form-label"
            >First Name</label
          >
          <Field
            name="registered_agent_first_name"
            type="text"
            class="form-control"
            autocomplete="given-name"
          />
          <ErrorMessage
            name="registered_agent_first_name"
            class="text-danger"
          />
        </div>
        <div class="col-md-4 mb-3">
          <label for="registered_agent_middle_name" class="form-label"
            >Middle Name</label
          >
          <Field
            name="registered_agent_middle_name"
            type="text"
            class="form-control"
            autocomplete="additional-name"
          />
          <ErrorMessage
            name="registered_agent_middle_name"
            class="text-danger"
          />
        </div>
      </div>
    </template>

    <!-- Corporate Agent Fields -->
    <template v-if="currentAgentType === 'C'">
      <div class="mb-3">
        <label for="registered_agent_name" class="form-label"
          >Business to serve as RA</label
        >
        <Field
          name="registered_agent_name"
          type="text"
          class="form-control"
          autocomplete="organization"
        />
        <div class="form-text">
          Enter the full legal name of the corporation including suffix (Inc.,
          Corp., LLC, etc.)
        </div>
        <ErrorMessage name="registered_agent_name" class="text-danger" />
      </div>
    </template>

    <!-- Signature Section -->
    <div v-if="hasChanges" class="mb-4 p-3 bg-warning-subtle border rounded">
      <p class="mb-3">
        If there is a change in registered agent, the new agent will need to
        type their name in the 'Registered Agent Signature' block below to
        accept the designation of registered agent. RA signature must be an
        individual name.
      </p>
      <p class="mb-3">
        I am aware that false information submitted in a document to the
        Department of State constitutes a third degree felony as provided for in
        s. 817.155, F.S. If the RA is a business entity, an individual must sign
        on their behalf. A business entity cannot serve as its own RA.
      </p>

      <div class="mb-3">
        <label for="registered_agent_signature" class="form-label"
          >Registered Agent Signature</label
        >
        <Field
          name="registered_agent_signature"
          type="text"
          class="form-control"
          placeholder="Type Name of Individual Signing Here"
          autocomplete="off"
        />
        <div class="form-text">
          This signature must be that of the individual "signing" this document
          electronically or be made with the full knowledge and permission of
          the individual, otherwise it constitutes forgery under s. 831.06, F.S.
        </div>
        <ErrorMessage name="registered_agent_signature" class="text-danger" />
      </div>
    </div>

    <!-- Address Fields Section -->
    <div class="address-section mb-4">
      <!-- Address Line 1 -->
      <div class="mb-3">
        <label for="registered_agent_address_1" class="form-label"
          >Street Address</label
        >
        <Field
          name="registered_agent_address_1"
          type="text"
          class="form-control"
          placeholder="Enter street address"
          autocomplete="address-line1"
        />
        <div class="form-text">
          Must be a physical street address (No P.O. Boxes or virtual addresses)
        </div>
        <ErrorMessage name="registered_agent_address_1" class="text-danger" />
      </div>

      <!-- Address Line 2 -->
      <div class="mb-3">
        <label for="registered_agent_address_2" class="form-label"
          >Apartment, suite, unit, etc. (optional)</label
        >
        <Field
          name="registered_agent_address_2"
          type="text"
          class="form-control"
          placeholder="Enter suite, unit, or apartment number"
          autocomplete="address-line2"
        />
        <ErrorMessage name="registered_agent_address_2" class="text-danger" />
      </div>

      <!-- City, State, Zip Row -->
      <div class="row">
        <!-- City -->
        <div class="col-md-5 mb-3">
          <label for="registered_agent_city" class="form-label">City</label>
          <Field
            name="registered_agent_city"
            type="text"
            class="form-control"
            placeholder="Enter city"
            autocomplete="address-level2"
          />
          <ErrorMessage name="registered_agent_city" class="text-danger" />
        </div>

        <!-- State -->
        <div class="col-md-3 mb-3">
          <label for="registered_agent_state" class="form-label">State</label>
          <Field
            as="select"
            name="registered_agent_state"
            class="form-select"
            autocomplete="address-level1"
          >
            <option value="">Select a state</option>
            <option
              v-for="choice in stateChoices"
              :key="choice.id"
              :value="choice.id"
            >
              {{ choice.name }}
            </option>
          </Field>
          <div class="form-text text-muted small">Select any US state</div>
          <ErrorMessage name="registered_agent_state" class="text-danger" />
        </div>

        <!-- Zip Code -->
        <div class="col-md-4 mb-3">
          <label for="registered_agent_zip" class="form-label">ZIP Code</label>
          <Field
            name="registered_agent_zip"
            type="text"
            class="form-control"
            placeholder="XXXXX or XXXXX-XXXX"
            autocomplete="postal-code"
          />
          <div class="form-text text-muted small">
            Enter a valid US ZIP code (5 digits or 5+4 format)
          </div>
          <ErrorMessage name="registered_agent_zip" class="text-danger" />
        </div>
      </div>
    </div>
  </form>
</template>
